/**
 * Backup Policy API Service
 *
 * WQ-036: Tier-Based Backup Policy (ADR-011)
 * API client for tier policy CRUD, test-connection, and tenant overrides.
 *
 * @module services/backupPolicyService
 */

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

// =============================================================================
// Types
// =============================================================================

export interface BackupPolicy {
  id: string;
  name: string;
  tier: string;
  frequency: string;
  retention_count: number;
  retention_days: number;
  weekly_retention_count?: number;
  monthly_retention_count?: number;
  scope: Record<string, boolean>;
  destination_type: string;
  destination_config: Record<string, string>;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface BackupPolicyUpdate {
  name?: string;
  frequency?: string;
  retention_count?: number;
  retention_days?: number;
  weekly_retention_count?: number;
  monthly_retention_count?: number;
  scope?: { database: boolean; files: boolean; configs: boolean };
  destination_type?: string;
  destination_config?: Record<string, string>;
  enabled?: boolean;
}

export interface TestConnectionResult {
  success: boolean;
  message: string;
}

export interface EffectiveConfig {
  source: string;
  policy_tier: string | null;
  frequency: string;
  retention_count: number;
  retention_days: number;
  weekly_retention_count?: number;
  monthly_retention_count?: number;
  scope: Record<string, boolean>;
  destination_type: string;
  destination_config: Record<string, string>;
  enabled: boolean;
}

export interface TenantOverrideRequest {
  enabled?: boolean;
  frequency?: string;
  retention_count?: number;
  retention_days?: number;
  weekly_retention_count?: number;
  monthly_retention_count?: number;
  scope?: { database: boolean; files: boolean; configs: boolean };
  destination_type?: string;
  destination_config?: Record<string, string>;
  policy_tier?: string;
}

const BASE = '/api/admin/backups';

// =============================================================================
// Policy CRUD
// =============================================================================

export const listPolicies = async (
  authFetch: AuthFetch,
): Promise<BackupPolicy[]> => {
  const response = await authFetch(`${BASE}/policies`);
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to fetch policies');
  }
  return response.json();
};

export const getPolicy = async (
  authFetch: AuthFetch,
  tier: string,
): Promise<BackupPolicy> => {
  const response = await authFetch(`${BASE}/policies/${tier}`);
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to fetch policy');
  }
  return response.json();
};

export const updatePolicy = async (
  authFetch: AuthFetch,
  tier: string,
  data: BackupPolicyUpdate,
): Promise<BackupPolicy> => {
  const response = await authFetch(`${BASE}/policies/${tier}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to update policy');
  }
  return response.json();
};

// =============================================================================
// Test Connection
// =============================================================================

export const testConnection = async (
  authFetch: AuthFetch,
  destinationType: string,
  destinationConfig: Record<string, string>,
): Promise<TestConnectionResult> => {
  const response = await authFetch(`${BASE}/policies/test-connection`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      destination_type: destinationType,
      destination_config: destinationConfig,
    }),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to test connection');
  }
  return response.json();
};

// =============================================================================
// Effective Config & Overrides
// =============================================================================

export const getEffectiveConfig = async (
  authFetch: AuthFetch,
  tenantId: string,
): Promise<EffectiveConfig> => {
  const response = await authFetch(`${BASE}/tenants/${tenantId}/effective-config`);
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to fetch effective config');
  }
  return response.json();
};

export const setTenantOverride = async (
  authFetch: AuthFetch,
  tenantId: string,
  data: TenantOverrideRequest,
): Promise<unknown> => {
  const response = await authFetch(`${BASE}/tenants/${tenantId}/override`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to set override');
  }
  return response.json();
};

export const removeTenantOverride = async (
  authFetch: AuthFetch,
  tenantId: string,
): Promise<{ success: boolean; message: string }> => {
  const response = await authFetch(`${BASE}/tenants/${tenantId}/override`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to remove override');
  }
  return response.json();
};
